import { useState, useEffect } from 'react';
import { useAuth } from '../../../auth';
import type { Room } from '@modelcast/types';
import { EventEditModal } from './EventEditModal';

type EventStatus = 'draft' | 'published' | 'live' | 'closed';

export function EventsPage() {
  const { getToken } = useAuth();
  const [events, setEvents] = useState<Room[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<Room | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | ''>('');
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchEvents();
  }, [search, statusFilter, page]);

  async function fetchEvents() {
    setIsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', String(limit));
      params.set('offset', String(page * limit));

      const response = await fetch(`/api/admin/events?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.rooms);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(code: string) {
    if (!confirm('Are you sure you want to delete this event? This cannot be undone.')) return;

    const note = window.prompt('Audit note for deleting this event (required):');
    if (note === null) return;
    if (!note.trim()) {
      alert('An audit note is required to delete an event.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/events/${code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: note.trim() }),
      });

      if (response.ok) {
        fetchEvents();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  }

  function handleEventUpdated() {
    setSelectedEvent(null);
    fetchEvents();
  }

  function getEventStatus(room: Room): EventStatus {
    if (room.closedAt) return 'closed';
    if (room.startedAt) return 'live';
    if (room.isPublished) return 'published';
    return 'draft';
  }

  const statusColors: Record<EventStatus, string> = {
    draft: 'bg-text-faint/20 text-text-dim',
    published: 'bg-accent/20 text-accent',
    live: 'bg-ok/20 text-ok',
    closed: 'bg-text-faint/20 text-text-dim',
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Events</h1>
          <p className="text-sm text-text-dim mt-1">{total} total events</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <input
            type="text"
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="input w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as EventStatus | ''); setPage(0); }}
          className="input w-auto"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="live">Live</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-panel rounded-xl border border-line p-8 text-center">
          <div className="text-text-dim">No events found</div>
        </div>
      ) : (
        <>
          <div className="bg-panel rounded-xl border border-line overflow-hidden">
            <table className="w-full">
              <thead className="bg-panel2 border-b border-line">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Event</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Code</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Scheduled</th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono text-text-faint uppercaser">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => {
                  const status = getEventStatus(event);
                  return (
                    <tr key={event.id} className="border-b border-line last:border-0 hover:bg-panel2/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {event.imageUrl ? (
                            <img
                              src={event.imageUrl}
                              alt=""
                              className="w-12 h-8 rounded object-cover"
                            />
                          ) : (
                            <div className="w-12 h-8 rounded bg-panel2 flex items-center justify-center">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-faint">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                              </svg>
                            </div>
                          )}
                          <div>
                            <div className="text-text text-sm font-medium">{event.name}</div>
                            <div className="flex items-center gap-2 text-xs text-text-dim">
                              {event.isPublic && <span>Public</span>}
                              {event.maxAttendees && <span>Max {event.maxAttendees}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-text-dim bg-panel2 px-1.5 py-0.5 rounded">
                          {event.code}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium uppercase ${statusColors[status]}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-dim">
                        {event.scheduledAt
                          ? new Date(event.scheduledAt).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedEvent(event)}
                            className="btn btn-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(event.code)}
                            className="btn btn-sm text-red-500 border-red-500/30 hover:bg-red-500/10"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-text-dim">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedEvent && (
        <EventEditModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onSaved={handleEventUpdated}
        />
      )}
    </div>
  );
}
